// @ts-strict

import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { IChosenSortValue, IPagination } from '@app/core/models/core-models';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { StatisticsTableItem } from '@services/statistics/statistics.types';
import { FilterMenuOptions } from '@pages/statistics/shared/interfaces/statistics';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { ColumnManagementOptionsType } from '../shared-statistics-functions/shared-statistics-functions.component';
import { AvailableStatisticsTableColumns, TableService } from './table.service';
import { dropdownValues } from '@app/core/helpers/constants';
import { toNumber } from 'lodash';
import { LeadsStatisticsItem } from '@pages/statistics/pages/leads/interfaces/leads-statistics';
import { ConfigurationsStatisticsItem } from '@pages/statistics/pages/configurations/interfaces/configurations-statistics';
import { CampaignsStatisticsItem } from '@pages/statistics/pages/campaigns-statistics/interfaces/campaign-statistics';

@Component({
  selector: 'app-statistics-table',
  templateUrl: './statistics-table.component.html',
  styleUrls: ['./statistics-table.component.scss']
})
export class StatisticsTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() statisticsData?: any;
  @Input() statisticsCompareData?: StatisticsTableItem[];
  @Input() title: string = '';
  @Input() paramToCompare: string = '';
  @Input() datesDifference: number = 0;
  @Input() compareToCurrentDatesDifference: number = 0;
  @Input() filterMenuOptions?: FilterMenuOptions;
  @Input() page: number = 1;
  @Input() pageSize: number = 10;
  @Input() inputPage?: number;
  @Input() isExpandable?: boolean;
  @Input() columnManagementOptions?: ColumnManagementOptionsType;
  @Input() totalNumberOfPages?: number;
  @Input() defaultVisibleColumns?: string[];
  @Input() loadTableDataOnPageChange?: boolean;
  @Input() sortDropdownValues?: IChosenSortValue[];
  @Input() pagination?: IPagination;
  @Input() appliedFiltersBar?: any;
  @Input() widgetMode?: boolean;

  @Output() emitPageChange = new EventEmitter<number>();
  @Output() emitItemsPerPage = new EventEmitter<number>();

  public listHeight = 510;
  public loadingNextPage = false;
  public throttle = 0;
  public scrollDistance = 2;
  public scrolledToBottom = false;

  public isDetailsTableLayout = false;
  public compareMode = false;

  public firstColumn?: AvailableStatisticsTableColumns;
  public activeSortMethod?: IChosenSortValue;
  public currency?: string;
  public currentOpenMenu?: string;

  public openedColumMenuHeight: number = 0;

  public activeColumnsNames$?: Observable<AvailableStatisticsTableColumns[]>;

  public filteredDataLength = 10;
  public minWidth = 1390;

  public campaignMenu = false;
  public leadIDMenu = false;
  public visitIDMenu = false;
  public statusMenu = false;
  public leadsMenu = false;
  public earnsMenu = false;
  public configurationNameMenu = false;
  public visitsMenu = false;
  public conversionMenu = false;
  public acceptanceMenu = false;
  public epcMenu = false;
  public rateMenu = false;
  public countriesMenu = false;
  public refferingMenu = false;
  public mlSubMenu = false;
  public salesMenu = false;
  public registrationAtMenu = false;
  public userNameMenu = false;

  public perPageValues: (typeof dropdownValues)[number][] = [dropdownValues[24], dropdownValues[25], dropdownValues[26], dropdownValues[27]];
  public numberOfItems: IChosenSortValue = dropdownValues[26];
  public shownPositionsFrom = 1;
  public shownPositionsTo = 50;

  public Object = Object;

  private destroy$ = new Subject<boolean>();

  get isDashboardRoute(): boolean {
    return this.router.url.includes('dashboard');
  }

  get isMenuClickable(): boolean {
    return true;
  }

  get isAdditionalDataVisible(): boolean {
    // return this.columnManagementOptions?.[2]?.results?.[3]?.checked;
    return this.statisticsTableService.tableColumns$.getValue()[2].results[3].checked;
  }

  constructor(
    private readonly statistics: StatisticsSummaryService,
    private readonly router: Router,
    private readonly statisticsTableService: TableService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.title === 'leads' || this.title === 'visits') {
      // this.title === 'configuration_stats' || this.title === 'campaigns_details_stats'
      this.perPageValues = [...this.perPageValues, dropdownValues[75], dropdownValues[76]];
      this.numberOfItems = dropdownValues[27];
      this.shownPositionsTo = 100;
    }
    // i've commented this coz it was overwriting default values from some statistics categories, also overwriting sort methods for main widget
    // doesn't look like this code is relevant anymore
    // if (this.sortDropdownValues) {
    //   this.activeSortMethod = this.sortDropdownValues[0];
    //   this.statisticsTableService.setTableSortMethod(this.activeSortMethod);
    //   this.statisticsTableService.setSortMethod(this.activeSortMethod);
    // }

    this.statisticsTableService
      .getSortMethod()
      .pipe(
        tap(sortMethod => (this.activeSortMethod = sortMethod)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.compareMode
      .pipe(
        tap(({ compareMode }) => (this.compareMode = compareMode)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.isDetailsLayout
      .pipe(
        tap(res => {
          this.isDetailsTableLayout = res;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.activeColumnsNames$ = this.statisticsTableService.tableColumns$.pipe(
      map(activeColumns => {
        return activeColumns
          .map(({ results }) => results)
          .flat()
          .filter(({ checked }) => checked)
          .map(({ column }) => column);
      }),
      tap(activeColumns => (this.firstColumn = activeColumns[0]))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.statisticsData) {
      this.currency = this.statisticsData[0]?.currency;
    }
    this.setShownPositions();
    if (changes.inputPage && changes.inputPage.previousValue < changes.inputPage.currentValue) {
      this.loadingNextPage = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public countTotalForLeads(): string {
    let rate = 0;
    (this.statisticsData as LeadsStatisticsItem[]).map(item => {
      rate += item.amount;
    });
    return rate.toFixed(2) + ' ' + this.currency;
  }

  public countTotalForLeadsAdditionalData(): { deposit: string; ngr: string; turnover: string } {
    let deposit = 0;
    let ngr = 0;
    let turnover = 0;
    (this.statisticsData as LeadsStatisticsItem[]).map(item => {
      if (item.additional_data?.deposit) {
        const depositValue = parseFloat(item.additional_data.deposit.replace(/[^0-9.-]+/g, ''));
        deposit += isNaN(depositValue) ? 0 : depositValue;
      }
      if (item.additional_data?.ngr) {
        const ngrValue = parseFloat(item.additional_data.ngr.replace(/[^0-9.-]+/g, ''));
        ngr += isNaN(ngrValue) ? 0 : ngrValue;
      }
      if (item.additional_data?.turnover) {
        const turnoverValue = parseFloat(item.additional_data.turnover.replace(/[^0-9.-]+/g, ''));
        turnover += isNaN(turnoverValue) ? 0 : turnoverValue;
      }
    });
    return {
      deposit: deposit.toFixed(2) + ' ' + this.currency,
      ngr: ngr.toFixed(2) + ' ' + this.currency,
      turnover: turnover.toFixed(2) + ' ' + this.currency
    };
  }

  public countTotalForPrograms(): string {
    let rate = 0;
    (this.statisticsData as ConfigurationsStatisticsItem[]).map(item => {
      rate += toNumber(item.earnings_status_payment_waiting) + toNumber(item.earnings_status_to_payment) + toNumber(item.earnings_status_wait);
    });
    return rate.toFixed(2) + ' ' + this.currency;
  }

  public countTotalForCampaigns(): string {
    let rate = 0;
    (this.statisticsData as CampaignsStatisticsItem[]).map(item => {
      rate += toNumber(item.earnings_status_payment_waiting) + toNumber(item.earnings_status_to_payment) + toNumber(item.earnings_status_wait);
    });
    return rate.toFixed(2) + ' ' + this.currency;
  }

  public pageChange(page: number): void {
    this.emitPageChange.emit(page);
  }

  public setPage(page: number): void {
    this.emitPageChange.emit(page);
  }

  public setItemsPerPage(value: IChosenSortValue): void {
    this.numberOfItems = value;
    this.emitItemsPerPage.emit(toNumber(value.name));
  }

  public onScroll(): void {
    if (
      this.pagination?.total_pages &&
      this.pagination.current_page &&
      this.pagination.total_pages > 1 &&
      this.pagination.current_page + 1 <= this.pagination.total_pages
    ) {
      this.loadingNextPage = true;
      this.emitPageChange.emit(this.pagination.current_page + 1);
    }
  }

  public maskVisibleHandler(): void {
    const parentElement = document.getElementById('list-wrapper-' + this.title);
    if (parentElement) {
      const scrollDifference = parentElement?.scrollTop - (parentElement?.scrollHeight - parentElement?.clientHeight);
      this.scrolledToBottom = scrollDifference > -20 && !this.loadingNextPage;
    }
  }

  private setShownPositions(): void {
    this.shownPositionsTo = (this.pagination?.current_page || 1) * parseInt(this.numberOfItems?.name || '0', 10);
    this.shownPositionsFrom = this.shownPositionsTo - parseInt(this.numberOfItems?.name || '0', 10) + 1;
  }

  public saveOpenedColumnMenuHeight = (height: number) => {
    this.openedColumMenuHeight = height;
    this.cdRef.detectChanges();
  };

  public redirectHandler(type: string): void {
    if (type === 'smartlinks') {
      this.router.navigate(['/affiliate-marketing/affiliate-campaigns'], {
        queryParams: { is_smartlink: 1 }
      });
    }
    if (type === 'incent') {
      this.router.navigate(['/affiliate-marketing/affiliate-campaigns'], {
        queryParams: { 'incent_type[]': 4 }
      });
    }
    if (type === 'hidelink') {
      this.router.navigate(['/tools/hidelink']);
    }
    if (type === 'referrals') {
      this.router.navigate(['/community/recommendations-system']);
    }
  }
}
